import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = 'https://gut-buddy-assets.s3.ap-south-1.amazonaws.com/privacy_policy.pdf';
    link.download = 'Gut-Buddy-Privacy-Policy.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="privacy-policy-page">
      <header className="header">
        <div className="container">
          <div className="header-content">
            <a href="/" className="logo">
              🏥 Gut Buddy
            </a>
            <nav>
              <a href="#privacy" className="nav-link">Privacy Policy</a>
            </nav>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="container">
          <div className="privacy-policy-container">
            <div className="privacy-header">
              <h1 className="privacy-title">Privacy Policy</h1>
              <p className="privacy-subtitle">
                Your privacy is important to us. Learn how we collect, use, and protect your information.
              </p>
            </div>

            <div className="privacy-content">
              <div className="privacy-text">
                <div className="last-updated">
                  <strong>Last Updated:</strong> December 2024
                </div>

                <section className="privacy-section">
                  <h2>1. Introduction</h2>
                  <p>
                    The mobile application "Gut-Buddy" (hereinafter referred to as "App") is owned and operated by Gut-Buddy Healthcare Services Pvt. Ltd. ("Company").
                  </p>
                  <p>
                    The Company is committed to the protection of your privacy and your personal information in accordance with applicable law.
                  </p>
                  <p>
                    This privacy policy ('Privacy Policy') discloses the Company's practices for Gut-Buddy including the type of information being collected, the method of such information collection, use of such information and manner of sharing such information. As per this Privacy Policy the Company protects personal information about the End-Users of the Services (as defined in the terms and conditions), the doctors/hospitals registered with Gut-Buddy ('Registered Doctor(s)/Hospital(s)'), and the visitors of the App (jointly and severally referred to as 'you' or 'User(s)' in this Privacy Policy).
                  </p>
                  <p>
                    By browsing, visiting, accessing and/or using the services available at Gut-Buddy App (or searching for any of the pages on the App), you confirm that you are bound by this Privacy Policy, or by using the Services, or by otherwise giving us your information, you agree to the practices and policies outlined in this Privacy Policy and you hereby consent to our collection, use and sharing of your information as described in this Privacy Policy.
                  </p>
                </section>

                <section className="privacy-section">
                  <h2>2. Why This Privacy Policy?</h2>
                  <p>This Privacy Policy is published in compliance with:</p>
                  <ul>
                    <li>Section 43A of the Information Technology Act, 2000</li>
                    <li>Regulation 4 of the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Information) Rules, 2011</li>
                    <li>Information Technology (Intermediaries Guidelines) Rules, 2011</li>
                  </ul>
                  <p>This Privacy Policy states the following:</p>
                  <ul>
                    <li>The type of information collected from the Users, including sensitive personal data or information</li>
                    <li>The purpose, means and modes of usage of such information</li>
                    <li>How and to whom such information will be disclosed</li>
                    <li>How the information collected is going to be protected</li>
                  </ul>
                </section>

                <section className="privacy-section">
                  <h2>3. What Personal Information Do We Collect?</h2>
                  <p>We collect the following types of information:</p>
                  
                  <h3>3.1 Contact Information</h3>
                  <p>Personally identifiable information such as:</p>
                  <ul>
                    <li>Name, email address, and phone number</li>
                    <li>Demographic data (gender, birthday/age, city, country)</li>
                    <li>Account credentials and profile information</li>
                  </ul>

                  <h3>3.2 Health and Medical Information</h3>
                  <p>Information related to your health and wellness:</p>
                  <ul>
                    <li>Personal data such as height, weight, ethnicity, food preferences and lifestyle</li>
                    <li>Health data including symptom tracking, dietary information, meal timings</li>
                    <li>Stress levels, mood, and overall wellbeing</li>
                    <li>Physical activity data and sleep patterns</li>
                    <li>Bowel movement details and frequency</li>
                    <li>Medical reports, prescriptions, and diagnostic test results</li>
                    <li>Medical history including chronic health conditions</li>
                  </ul>

                  <h3>3.3 Usage Information</h3>
                  <p>Data regarding your use of our services:</p>
                  <ul>
                    <li>App usage patterns and interactions</li>
                    <li>User engagement with educational content and features</li>
                    <li>Feedback, ratings, and support communications</li>
                    <li>Device information and technical data</li>
                  </ul>
                </section>

                <section className="privacy-section">
                  <h2>4. How Do We Use Your Information?</h2>
                  <p>We use your information for the following purposes:</p>
                  <ul>
                    <li>To provide and improve our health tracking services</li>
                    <li>To personalize your experience and recommendations</li>
                    <li>To connect you with healthcare providers</li>
                    <li>To generate insights about your health patterns</li>
                    <li>To send you relevant health tips and updates</li>
                    <li>To ensure the security and proper functioning of our app</li>
                    <li>To comply with legal obligations</li>
                  </ul>
                </section>

                <section className="privacy-section">
                  <h2>5. Information Sharing and Disclosure</h2>
                  <p>We may share your information in the following circumstances:</p>
                  <ul>
                    <li><strong>With Healthcare Providers:</strong> When you choose to share your data with registered doctors or hospitals</li>
                    <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                    <li><strong>Service Providers:</strong> With trusted third parties who help us operate our services</li>
                    <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                  </ul>
                </section>

                <section className="privacy-section">
                  <h2>6. Data Security</h2>
                  <p>We implement appropriate technical and organizational measures to protect your personal information:</p>
                  <ul>
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security assessments and updates</li>
                    <li>Access controls and authentication measures</li>
                    <li>Secure data storage and backup procedures</li>
                    <li>Employee training on data protection</li>
                  </ul>
                </section>

                <section className="privacy-section">
                  <h2>7. Your Rights and Choices</h2>
                  <p>You have the following rights regarding your personal information:</p>
                  <ul>
                    <li><strong>Access:</strong> Request a copy of your personal data</li>
                    <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                    <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                    <li><strong>Portability:</strong> Receive your data in a structured format</li>
                    <li><strong>Withdrawal:</strong> Withdraw consent for data processing</li>
                    <li><strong>Objection:</strong> Object to certain types of data processing</li>
                  </ul>
                </section>

                <section className="privacy-section">
                  <h2>8. Data Retention</h2>
                  <p>We retain your personal information for as long as necessary to:</p>
                  <ul>
                    <li>Provide our services to you</li>
                    <li>Comply with legal obligations</li>
                    <li>Resolve disputes and enforce agreements</li>
                    <li>Improve our services and develop new features</li>
                  </ul>
                  <p>When you delete your account, we will delete or anonymize your personal data within 30 days, unless we are required to retain it for legal reasons.</p>
                </section>

                <section className="privacy-section">
                  <h2>9. Children's Privacy</h2>
                  <p>Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.</p>
                </section>

                <section className="privacy-section">
                  <h2>10. International Data Transfers</h2>
                  <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.</p>
                </section>

                <section className="privacy-section">
                  <h2>11. Changes to This Privacy Policy</h2>
                  <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by:</p>
                  <ul>
                    <li>Posting the updated policy on our app</li>
                    <li>Sending you an email notification</li>
                    <li>Displaying a prominent notice in our app</li>
                  </ul>
                  <p>Your continued use of our services after any changes constitutes acceptance of the updated Privacy Policy.</p>
                </section>

                <section className="privacy-section">
                  <h2>12. Contact Us</h2>
                  <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
                  <div className="contact-info">
                    <p><strong>Gut-Buddy Healthcare Services Pvt. Ltd.</strong></p>
                    <p>Email: privacy@gutbuddy.com</p>
                    <p>Phone: +91-XXXX-XXXX</p>
                    <p>Address: [Company Address]</p>
                  </div>
                </section>
              </div>

              <div className="download-section">
                <h3>Download Privacy Policy</h3>
                <p>You can download a copy of our privacy policy for your records.</p>
                <button onClick={handleDownload} className="download-button">
                  📄 Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <p>&copy; 2024 Gut Buddy. All rights reserved.</p>
            </div>
            <div className="footer-links">
              <a href="#privacy">Privacy Policy</a>
              <a href="#terms">Terms of Service</a>
              <a href="#contact">Contact Us</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
